import { detectTruncation } from '#utils/PRIVATE_truncationUtils';
import { useResizeObserver } from '#hooks/UNSAFE_useResizeObserver';
import { useCallback, useRef, useState } from 'preact/hooks';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';

/**
 * The useDetectTruncation hook uses a ResizeObserver to determine when to measure the container it's attached to, and uses a rootRef that is an HTMElement to attach to the container, or a different type can be passed in when the hook is called.
 */
const useDetectTruncation = <E extends HTMLElement = HTMLElement>(isDisabled = false) => {
  const rootRef = useRef<E>(null);
  const [isTextTruncated, setIsTextTruncated] = useState<boolean>(false);
  const { isTabbable } = useTabbableMode();
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const detectIfTruncated = useCallback(() => {
    if (rootRef.current && !isDisabled) {
      const element = rootRef.current;
      element.addEventListener('mouseenter', handleMouseEnter);
      element.addEventListener('mouseleave', handleMouseLeave);
      if (isTabbable || isHovered) {
        const isTruncated = detectTruncation(rootRef);
        setIsTextTruncated(isTruncated);
      }
    }
  }, [isDisabled, isHovered, isTabbable]);
  useResizeObserver(rootRef, detectIfTruncated, { isDisabled: isDisabled });

  const detectTruncationProps = {
    ref: rootRef
  };

  return { detectTruncationProps, isTextTruncated };
};

export { useDetectTruncation };
